/**
 * A tested granny flat plan in everyday words — the card's title and the line under it (granny flat rebuild,
 * 21 Oct 2026; the agreed mock-up's plans screen, as the village's rvPlanWords).
 *
 * The engine's names ("Pay the assessed value and rent out your home") were written for an adviser's list.
 * The card says who is paid and how much, where the money comes from, and what happens to the home:
 * "Pay your child $398,316 from savings; keep the home and rent it out", "$150,000 from the bank and
 * $248,316 from the account-based pension. None of it is a gift. The home is let for about $78,000 a year."
 *
 * The words are read from the plan's own inputs (what is paid, signed over or built, the funding rows, the
 * home, what a sale's money goes into), so a plan built by hand reads the same way as a tested one. Nothing
 * here is computed beyond sums; every figure is the engine row's own.
 */
import { formatCurrency } from '../../utils/index.ts';
import type { LedgerChange } from '../../components/strategy/CauseEffectLedger.tsx';
import { householdVoice } from '../../utils/householdWords.ts';
import { investedWhereWords } from '../../utils/investedWhere.ts';
import { EXTRA_ALLOWABLE_AMOUNT, GIFTING_LIMIT_ANNUAL } from '../../constants/index.ts';
import { gfHostPhrase, gfHostHome } from './gfPresentation.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { loansSecuredOnHome } from '../../../../shared/src/calculation/grannyFlatArrangement.ts';

/** "the $X home" — and, with a loan on it, that the family member takes the loan over (24 Oct 2026). */
function homeSignedOverWords(given: number, factFind: DraftFactFind | null | undefined): string {
    const loans = loansSecuredOnHome(factFind as never);
    return loans >= 1
        ? `home, ${money(given)} after the ${money(loans)} loan the family member takes over with it,`
        : `${money(given)} home`;
}

export interface GfPlanLike {
    id?: string | null;
    name?: string | null;
    inputs?: Record<string, unknown> | null;
    fundingBreakdown?: Array<{ label: string; amount: number }> | null;
    deprivation?: number | null;
    [key: string]: unknown;
}

const num = (v: unknown): number => parseFloat(String(v ?? '')) || 0;
const money = (n: number) => formatCurrency(Math.round(n));
const joinAnd = (xs: string[]) => (xs.length <= 1 ? xs[0] || '' : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** What the plan does, read off its inputs exactly as the evaluator did. */
export interface GfMove {
    amountPaid: number;
    propertyTransferred: number;
    construction: number;
    /** Everything given for the right: cash, the home's value and the build. */
    entryContribution: number;
    /** Above the extra allowable amount they are homeowners for the Age Pension. */
    isHomeowner: boolean;
    selling: boolean;
    keeping: boolean;
    rented: boolean;
    /** The kept home's weekly rent as the engine charges it (after the weeks it sits empty). */
    keptHomeRent: number;
    sellingCosts: number;
    /** On a sale: what is left once the right is paid for. */
    saleResidual: number;
    justMovingIn: boolean;
    home: 'kept' | 'rented' | 'sold' | 'transferred' | null;
    deprivation: number;
}

export function gfMoveOf(plan: GfPlanLike | null | undefined, factFind: DraftFactFind | null | undefined): GfMove {
    const cfg = (plan?.inputs || {}) as Record<string, unknown>;
    const homeValue = num(factFind?.home?.value);
    const hasHome = homeValue > 0;
    const amountPaid = num(cfg.amountPaid);
    const propertyTransferred = num(cfg.propertyTransferredValue);
    const construction = num(cfg.constructionCost);
    const entryContribution = amountPaid + propertyTransferred + construction;
    const selling = cfg.fundFrom === 'sell_home' && homeValue > 0;
    const keeping = hasHome && propertyTransferred === 0 && !selling;
    const rented = keeping && cfg.keptHomeUse === 'rented';
    const sellingCosts = num(cfg.sellingCosts) || Math.round(homeValue * 0.025);
    const saleResidual = selling ? Math.max(0, homeValue - sellingCosts - amountPaid - construction) : 0;
    const justMovingIn = /^gf-move-in-only/.test(String(plan?.id || '')) || (entryContribution === 0 && cfg.fundFrom !== 'sell_home');
    const home: GfMove['home'] = !hasHome ? null : selling ? 'sold' : propertyTransferred > 0 ? 'transferred' : rented ? 'rented' : 'kept';
    return {
        amountPaid, propertyTransferred, construction, entryContribution, isHomeowner: entryContribution > EXTRA_ALLOWABLE_AMOUNT,
        selling, keeping, rented, keptHomeRent: num(cfg.keptHomeRent), sellingCosts, saleResidual, justMovingIn, home,
        deprivation: num(plan?.deprivation),
    };
}

/** What goes to the person they live with at the start: the cash, the home's value and the build. */
export function gfGivenOf(m: GfMove): number {
    return m.justMovingIn ? 0 : m.entryContribution;
}

/** "the bank", "the account-based pension" — the evaluator's funding rows in plain words. */
const SOURCE_WORDS: Array<[RegExp, string]> = [
    [/bank/i, 'the bank'],
    [/term deposit/i, 'term deposits'],
    [/account-based pension|pension/i, 'the account-based pension'],
    [/shares|managed funds/i, 'selling shares'],
    [/cash from your savings/i, 'savings'],
];

/** "$150,000 from the bank and $248,316 from the account-based pension" — where a savings payment comes from. */
export function gfDrawWords(rows: ReadonlyArray<{ label: string; amount: number }> | null | undefined): string {
    const parts: string[] = [];
    for (const r of rows || []) {
        if (!(num(r.amount) > 0)) continue;
        if (/home transferred|selling your home/i.test(r.label)) continue;
        const words = SOURCE_WORDS.find(([re]) => re.test(r.label))?.[1] ?? r.label.toLowerCase();
        parts.push(`${money(num(r.amount))} from ${words}`);
    }
    return joinAnd(parts);
}

/** What a sale's money is put into, short (for the title) and long (for the line). */
function saleUsesOf(d: Record<string, unknown>, factFind: DraftFactFind | null | undefined): { short: string[]; long: string[] } {
    const v = householdVoice(factFind);
    const short: string[] = [];
    const long: string[] = [];
    const sup = num(d.superContribution) + num(d.partnerSuperContribution);
    if (sup > 0) {
        short.push(`top up super by ${money(sup)}`);
        long.push(`${money(sup)} into super as an after-tax contribution${d.superCommenceIncomeStream === true ? ', drawn as a pension' : ''}`);
    }
    const annuity = num(d.annuity);
    if (annuity > 0) {
        const back = String(d.annuityType) === 'guaranteed'
            ? 'that pays the full amount back to the family at any time'
            : num(d.annuityResidualPercent) > 0 ? 'that pays the money back to the family in the early years' : 'that pays the most income, with nothing back to the family';
        // The kind in the title too: two plans differing only in it read alike (24 Oct 2026).
        const kind = String(d.annuityType) === 'guaranteed' ? ', full amount back' : num(d.annuityResidualPercent) > 0 ? ', money back early on' : ', income only';
        short.push(`put ${money(annuity)} into a guaranteed income${kind}`);
        long.push(`${money(annuity)} into a guaranteed income for life ${back}`);
    }
    const ip = num(d.investmentProperty);
    if (ip > 0) {
        short.push('buy an investment property');
        const duty = num(d.ipStampDuty);
        long.push(`${money(ip)} on an investment property${duty > 0 ? ` plus ${money(duty)} stamp duty` : ''}${num(d.ipRent) > 0 ? `, let for about ${money(num(d.ipRent))} a year` : ''}`);
    }
    const bond = num(d.investmentBond);
    if (bond > 0) {
        short.push(short.length ? 'put the rest in an investment bond' : 'put it in an investment bond');
        long.push(`${money(bond)} into an investment bond`);
    }
    const shares = num(d.shares);
    if (shares > 0) {
        short.push(short.length ? 'invest the rest' : 'invest what is left');
        long.push(`${short.length > 1 ? 'the other ' : ''}${money(shares)} invested ${investedWhereWords(factFind)}${v.couple ? `, in ${v.their} joint names` : ''}`);
    }
    return { short, long };
}

/** The card's title and sub-line for a granny flat plan, tested or built by hand. */
export function gfPlanWords(plan: GfPlanLike | null | undefined, factFind: DraftFactFind | null | undefined): { title: string; sub: string } {
    const v = householdVoice(factFind);
    const m = gfMoveOf(plan, factFind);
    const host = gfHostPhrase(factFind);
    const id = String(plan?.id || '');
    const debts = /-clear-debt$/.test(id) || (plan?.inputs as { debtPayoff?: unknown } | undefined)?.debtPayoff ? true : false;
    const debtsTitle = debts ? ', and clear the debts' : '';
    const debtsSub = debts ? ` ${v.Possessive} debts are cleared at the same time.` : '';
    const hasHome = num(factFind?.home?.value) > 0;
    const homeValue = num(factFind?.home?.value);
    const cfg = (plan?.inputs || {}) as Record<string, unknown>;

    // The gift as Centrelink counts it: above what the right is worth, less the $10,000 a year that can be given
    // freely (24 Oct 2026: the cards showed the gross figure, the calc counts the net one).
    const giftWords = m.deprivation > 0
        ? (gfGiftCounted(m.deprivation) > 0
            ? `${money(m.deprivation)} is above what the right is worth; ${money(gfGiftCounted(m.deprivation))} of it counts as a gift for five years, after the ${money(GIFTING_LIMIT_ANNUAL)} that can be given freely.`
            : `${money(m.deprivation)} is above what the right is worth, but within the ${money(GIFTING_LIMIT_ANNUAL)} a year that can be given freely, so nothing is counted.`)
        : m.amountPaid === 0 && (m.propertyTransferred > 0 || m.construction > 0)
            ? `Centrelink takes it at face value, so none of it is a gift${m.isHomeowner ? `, and ${v.they} stay homeowners` : `, but at ${money(m.entryContribution)} it is not more than ${money(EXTRA_ALLOWABLE_AMOUNT)}, so ${v.they} are not homeowners and the right counts as an asset`}.`
            : 'None of it is a gift.';
    const homeWords = m.home === 'rented'
        ? ` The home is let for about ${money(m.keptHomeRent * 52)} a year, after the weeks it sits empty.`
        : m.home === 'kept' ? ` The home is kept empty, and counted by Centrelink from the day ${v.they} move.` : '';

    if (m.justMovingIn) {
        return {
            title: `Move in with ${host} and pay nothing for the right${debtsTitle}`,
            sub: `No right is paid for, so nothing is a gift.${hasHome ? ` The home stops being exempt the day ${v.they} move, and is counted from then.` : ''}${debtsSub}`.trim(),
        };
    }

    if (m.propertyTransferred > 0) {
        const top = m.amountPaid + m.construction;
        return {
            title: `Sign the home over to ${host} for the right to live with them${top > 0 ? `, with ${money(top)} on top` : ''}${debtsTitle}`,
            sub: `The ${homeSignedOverWords(m.propertyTransferred, factFind)} pays for the right. ${giftWords}${debtsSub}`.trim(),
        };
    }

    if (m.selling) {
        const d = (cfg.deployment || {}) as Record<string, unknown>;
        const { short, long } = saleUsesOf(d, factFind);
        const pays = m.construction > 0 ? `pay ${money(m.construction)} for a build at ${gfHostHome(factFind)}${m.amountPaid > 0 ? ` and ${money(m.amountPaid)} in cash` : ''}` : `pay ${host} ${money(m.amountPaid)}`;
        const title = `Sell the home, ${pays}${short.length ? `, and ${joinAnd(short)}` : ', and keep the rest in the bank'}${debtsTitle}`;
        const left = Math.max(0, homeValue - m.sellingCosts - m.amountPaid - m.construction);
        const sub = `The sale pays for the right, after selling costs. ${giftWords} ${long.length ? `Of the ${money(left)} left: ${long.join('; ')}.` : `The ${money(left)} left stays in the bank.`}${debtsSub}`;
        return { title, sub: sub.trim() };
    }

    // Paid from savings, the home kept (or no home).
    const homeTitle = m.home === 'rented' ? '; keep the home and rent it out' : m.home === 'kept' ? '; keep the home as it is' : '';
    const pays = m.construction > 0
        ? `Pay ${money(m.construction)} for a build at ${gfHostHome(factFind)}${m.amountPaid > 0 ? `, plus ${money(m.amountPaid)} in cash` : ''}`
        : `Pay ${host} ${money(m.amountPaid)}`;
    const drawn = gfDrawWords(plan?.fundingBreakdown);
    // Two plans that differ only in the order the savings are drawn say which (owner's review, 24 Oct 2026: four
    // rows read "Pay their host $654,966 from savings; keep the home as it is"). The engine names the order.
    const order = String((plan as { name?: unknown } | null | undefined)?.name || '').split(' — from ')[1]?.replace(/, and clear your debts$/, '').trim();
    const title = `${pays} ${order ? `from ${order}` : 'from savings'}${homeTitle}${debtsTitle}`;
    const sub = `${drawn ? `${cap(drawn)}. ` : ''}${giftWords}${homeWords}${debtsSub}`;
    return { title, sub: sub.trim() };
}

/** Why a plan's Age Pension differs from not entering, in a few words. */
export function gfPensionReason(plan: GfPlanLike | null | undefined, factFind: DraftFactFind | null | undefined): string | null {
    const m = gfMoveOf(plan, factFind);
    const v = householdVoice(factFind);
    if (m.home === 'rented') return 'the rented home is counted';
    if (m.home === 'kept') return 'the empty home is counted';
    if (m.home === 'sold') return 'the sale money is counted';
    if (m.home === 'transferred') return null;
    if (m.entryContribution > 0 && !m.isHomeowner) return `${v.they} are no longer homeowners`;
    return null;
}

/**
 * "Explain this", as the mock-up numbers it: what they give and where it comes from, what happens to the home,
 * what that does to the Age Pension, the year's money put together, and "=" the estate after the horizon, with
 * what the person they live with received said beside it — not lost, and not in that figure.
 */
/** The part of a granny flat gift Centrelink counts: what is above the right's worth, less the yearly free area. */
export function gfGiftCounted(gross: number): number {
    return Math.max(0, (gross || 0) - GIFTING_LIMIT_ANNUAL);
}

export function gfExplainSteps(
    plan: GfPlanLike & { snapshot?: { pension?: number; netCashflow?: number; legacyValue?: number } | null },
    base: { snapshot?: { pension?: number; netCashflow?: number; legacyValue?: number } | null } | null | undefined,
    factFind: DraftFactFind | null | undefined,
    horizonYears: number,
): { steps: string[]; stepChanges: Array<{ before: string; change: LedgerChange } | null>; putTogether: string; total: string; givenNote: string | null; cashflow: number; baseCashflow: number | null; estate: number; baseEstate: number | null } {
    const v = householdVoice(factFind);
    const m = gfMoveOf(plan, factFind);
    const host = gfHostPhrase(factFind);
    const snap = plan.snapshot || {};
    const b = base?.snapshot || null;
    const steps: string[] = [];
    if (m.justMovingIn) {
        steps.push(`${v.Names} move${v.s} in with ${host} and pay nothing for the right, so nothing is a gift and no right is created.`);
    } else if (m.propertyTransferred > 0) {
        steps.push(`${v.Names} sign${v.s} the ${homeSignedOverWords(m.propertyTransferred, factFind)} over to ${host} for the right to live with them${m.amountPaid > 0 ? `, with ${money(m.amountPaid)} on top` : ''}. Centrelink takes the home at face value, so ${m.deprivation > 0 ? `${money(gfGiftCounted(m.deprivation))} of what is on top counts as a gift, after the ${money(GIFTING_LIMIT_ANNUAL)} that can be given freely` : 'none of it is a gift'}${m.isHomeowner ? `, and ${v.they} stay homeowners` : `; at ${money(m.entryContribution)} it is not more than ${money(EXTRA_ALLOWABLE_AMOUNT)}, so ${v.they} are not homeowners and the right counts as an asset`}.`);
    } else {
        const what = m.construction > 0 ? `${money(m.construction)} for a build at ${gfHostHome(factFind)}${m.amountPaid > 0 ? ` and ${money(m.amountPaid)} in cash` : ''}` : `${host} ${money(m.amountPaid)}`;
        const from = m.selling ? 'from the sale of the home' : gfDrawWords(plan.fundingBreakdown);
        const gift = m.deprivation > 0
            ? `${money(m.deprivation)} of it is above what the right is worth, so ${money(gfGiftCounted(m.deprivation))} counts as a gift for five years, after the ${money(GIFTING_LIMIT_ANNUAL)} that can be given freely`
            : m.amountPaid === 0 ? 'the build is taken at face value, so none of it is a gift' : 'it is within Centrelink’s limit for the right, so none of it is a gift';
        const owner = m.isHomeowner ? `, and it is more than ${money(EXTRA_ALLOWABLE_AMOUNT)}, so ${v.they} stay homeowners and the right is not counted` : `; it is not more than ${money(EXTRA_ALLOWABLE_AMOUNT)}, so ${v.they} are not homeowners for the Age Pension, and the right counts as an asset`;
        steps.push(`${v.Names} give${v.s} ${what} for the right to live with them${from ? ` — ${from}` : ''}. ${cap(gift)}${owner}.`);
    }
    if (m.home === 'rented') steps.push(`${v.They} keep ${v.their} home and rent it out for about ${money(m.keptHomeRent * 52)} a year, after the weeks it sits empty.`);
    else if (m.home === 'kept') steps.push(`${v.They} keep ${v.their} home, empty.`);
    else if (m.home === 'sold') {
        const d = ((plan.inputs || {}) as { deployment?: Record<string, unknown> }).deployment || {};
        const { long } = saleUsesOf(d, factFind);
        steps.push(`The home is sold; of the ${money(m.saleResidual)} left after the right is paid for, ${long.length ? long.join('; ') : 'all of it stays in the bank'}.`);
    }
    const pAfter = num(snap.pension);
    const pBefore = num(b?.pension);
    // The pension step's figure as data, for Explain this's colour (the steps keep their words for the document).
    const stepChanges: Array<{ before: string; change: LedgerChange } | null> = steps.map(() => null);
    if (b) {
        if (Math.abs(pAfter - pBefore) < 1) steps.push(`${v.Possessive} Age Pension is unchanged, ${money(pAfter)} a year.`);
        else if (m.home === 'rented' || m.home === 'kept') {
            steps.push(`The home is counted from the day ${v.they} move, so ${v.their} Age Pension ${pAfter < 1 ? 'stops' : 'falls'}: ${money(pBefore)} to ${money(pAfter)}.`);
            if (pAfter >= 1) stepChanges[steps.length - 1] = { before: `The home is counted from the day ${v.they} move, so ${v.their} Age Pension falls`, change: { from: pBefore, to: pAfter, unit: ' a year', better: 'up', after: '.' } };
        } else {
            steps.push(`${v.Possessive} Age Pension goes from ${money(pBefore)} to ${money(pAfter)} a year.`);
            stepChanges[steps.length - 1] = { before: `${v.Possessive} Age Pension goes`, change: { from: pBefore, to: pAfter, unit: ' a year', better: 'up', after: '.' } };
        }
    }
    while (stepChanges.length < steps.length) stepChanges.push(null);
    /*
     * THE "PUT TOGETHER" LINE AND THE "=" LINE ARE THE SHARED LEDGER'S (owner, 22 Oct 2026: "Explain this" as aged care
     * tells it, in every module). The steps above are the causes only; the figures for putTogetherLine and
     * CauseEffectLedger are returned beside them, against staying put — the plan's OWN staying-put run.
     */
    const cAfter = num(snap.netCashflow);
    const est = num(snap.legacyValue);
    const delta = b ? est - num(b.legacyValue) : null;
    const given = gfGivenOf(m);
    const givenNote = given > 0 ? `The ${money(given)} ${host} received is not in that figure.` : null;
    const putTogether = `Put together, ${v.they} have ${cAfter < 0 ? `${money(-cAfter)} a year short` : `${money(cAfter)} a year to spare`} in the first year${b ? ` (staying put, ${num(b.netCashflow) < 0 ? `${money(-num(b.netCashflow))} short` : `${money(num(b.netCashflow))} to spare`})` : ''}.`;
    const total = `After about ${horizonYears} ${horizonYears === 1 ? 'year' : 'years'} ${v.their} estate is ${money(est)}${delta != null ? ` — ${money(Math.abs(delta))} ${delta < 0 ? 'less' : 'more'} than staying put` : ''}.${givenNote ? ` ${givenNote}` : ''}`;
    return {
        steps, stepChanges, putTogether, total, givenNote,
        cashflow: cAfter, baseCashflow: b ? num(b.netCashflow) : null,
        estate: est, baseEstate: b ? num(b.legacyValue) : null,
    };
}

/** The plan as a short "-ing" phrase, for a sentence that names it: "every plan except signing the home over". */
export function gfPlanGerund(plan: GfPlanLike | null | undefined, factFind: DraftFactFind | null | undefined): string {
    const m = gfMoveOf(plan, factFind);
    if (m.justMovingIn) return 'moving in without paying';
    if (m.propertyTransferred > 0) return 'signing the home over';
    if (m.selling) return 'selling the home';
    if (m.construction > 0) return 'paying for a build';
    if (m.home === 'rented') return 'paying from savings and renting the home out';
    if (m.home === 'kept') return 'paying from savings and keeping the home empty';
    return 'paying from savings';
}
